import { CSSProperties } from "react";
import { t } from 'i18next';
import {
  IWebsiteBuilderNodeBase,
  IWebsiteBuilderNodeDimensions,
  IWebsiteBuilderNodePosition,
} from "../websiteBuilderNode";
import { IWebsiteBuilderButton } from "./websiteBuilderButton";
import { IWebsiteBuilderDivider } from "./websiteBuilderDivider";
import { IWebsiteBuilderInput } from "./websiteBuilderInput";
import { IWebsiteBuilderParagraph } from "./websiteBuilderParagraph";
import { IWebsiteBuilderSelect } from "./websiteBuilderSelect";
import { IWebsiteBuilderHeading } from "./websiteBuilderHeading";

export interface IWebsiteBuilderElementStretch {
  enabled: boolean;
  margin: number;
}

export interface IWebsiteBuilderElementDataBase {
  type: string;
  position: IWebsiteBuilderNodePosition;
  dimensions: IWebsiteBuilderNodeDimensions;
  zIndex: number;
}

export interface IWebsiteBuilderElementBase extends IWebsiteBuilderNodeBase {
  type: "element";
  element: IWebsiteBuilderElementDataBase;
}

export type TWebsiteBuilderElement =
  | IWebsiteBuilderButton
  | IWebsiteBuilderDivider
  | IWebsiteBuilderHeading
  | IWebsiteBuilderInput
  | IWebsiteBuilderParagraph
  | IWebsiteBuilderSelect;

export type TWebsiteBuilderElementData = TWebsiteBuilderElement["element"];
export type TWebsiteBuilderElementType = TWebsiteBuilderElementData["type"];
export type TWebsiteBuilderElementSettings = TWebsiteBuilderElementData["settings"];

type HasStretch<T> = T extends { stretch: IWebsiteBuilderElementStretch } ? T : never;
type TWebsiteBuilderStretchElementData = HasStretch<TWebsiteBuilderElementData>;
type TWebsiteBuilderStretchElementType = TWebsiteBuilderStretchElementData["type"];
const stretchElementTypes: TWebsiteBuilderStretchElementType[] = ["divider"];

export type TWebsiteBuilderTextElementType =
  | IWebsiteBuilderHeading["element"]["type"]
  | IWebsiteBuilderParagraph["element"]["type"];
const textElementTypes: TWebsiteBuilderTextElementType[] = ["heading", "paragraph"];

export function isWebsiteBuilderStretchElementData(
  element: TWebsiteBuilderElementData,
): element is TWebsiteBuilderStretchElementData {
  return !!stretchElementTypes.find(stretchElementType => stretchElementType === element.type);
}

export function isWebsiteBuilderTextElementType(
  type: TWebsiteBuilderElementType,
): type is TWebsiteBuilderTextElementType {
  return !!textElementTypes.find(textElementType => textElementType === type);
}

export function isWebsiteBuilderElementFromBase(node: IWebsiteBuilderNodeBase): node is TWebsiteBuilderElement {
  return node?.type === "element";
}

export interface IWebsiteBuilderElementDesignStyles {
  className: string;
  style: CSSProperties;
}

export const WEBSITE_BUILDER_ELEMENT_CLASS_NAME = "website-builder-element";

export const elementTitles: Record<TWebsiteBuilderElementType, string> = {
  button: t("Button"),
  divider: t("Divider"),
  heading: t("Heading"),
  input: t("Input"),
  paragraph: t("Paragraph"),
  select: t("Dropdown"),
};

export function getElementScreenPosition(
  leftX: number,
  position: IWebsiteBuilderNodePosition,
): IWebsiteBuilderNodePosition {
  return {
    x: leftX + position.x,
    y: position.y,
  };
}

export function getElementPosition(
  leftX: number,
  screenPosition: IWebsiteBuilderNodePosition,
): IWebsiteBuilderNodePosition {
  return {
    x: screenPosition.x - leftX,
    y: screenPosition.y,
  };
}
